import {backOff} from 'exponential-backoff';
import {
  ProductInfoV2,
  Session,
  SessionInfo,
  SuccessfulPaymentInfo,
} from './session';
import {EnvConfig} from '../config/envConfig.interface';

declare global {
  // https://developers.google.com/tag-platform/gtagjs/reference
  const gtag:
    | undefined
    | ((
        command: 'config' | 'get' | 'set' | 'event',
        param1: string | Record<string, unknown>,
        param2?: Record<string, unknown>,
      ) => void);
}

export interface ITrackingService {
  trackEvent(
    event: string,
    data?: Record<string, unknown>,
    callback?: () => void,
    timeout?: number,
  ): void;
  trackAddToCartEvent(callback?: () => void): void;
  trackBeginCheckoutEvent(callback?: () => void): void;
  trackPurchaseEvent(callback?: () => void): void;
}

export class GtmHelper implements ITrackingService {
  constructor(protected configuration: EnvConfig) {}

  private isConsentInitialized(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataLayer = (window as any)['dataLayer'];

    // Check if there is any consent_status event in the dataLayer.
    // This means consent has been initialized by usercentrics.
    // See: https://docs.usercentrics.com/#/v2-events?id=add-trigger
    return (
      Array.isArray(dataLayer) &&
      (dataLayer as {event: string}[]).some(entry => {
        return entry.event === 'consent_status';
      })
    );
  }

  public trackEvent(
    event: string,
    data?: Record<string, unknown>,
    callback?: () => void,
    timeout = 2000,
  ): void {
    const env = this.configuration;

    backOff(
      async () => {
        if (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any)['google_tag_manager'] &&
          gtag !== undefined &&
          this.isConsentInitialized()
        ) {
          return gtag;
        } else {
          throw new Error('gtm not available');
        }
      },
      {
        numOfAttempts: 20,
        maxDelay: 100,
      },
    )
      .then(gtag => {
        // create a failsafe timeout in case gtm never completes and the gtm-built-in timeout never fires
        const failsafeTimeout = setTimeout(() => {
          if (callback) {
            callback();
          }
        }, timeout);
        gtag('event', event, {
          event_callback: (id: string) => {
            // clear the failsafe timeout, so the callback is not called twice
            clearTimeout(failsafeTimeout);

            // prevent multiple calls by different containers
            if (id === env.gtmContainerId && callback) {
              callback();
            }
          },
          event_timeout: timeout,
          ...data,
        });
      })
      .catch(() => {
        if (callback) {
          callback();
        }
      });
  }

  public trackPurchaseEvent(callback?: () => void): void {
    const blueprintVariant = Session.instance.get(
      SessionInfo.purchase_blueprintVariant,
    );
    const productInfo = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    const paymentInfo = Session.instance.getObject<SuccessfulPaymentInfo>(
      SessionInfo.purchase_successfulPaymentInfo,
    );
    let data: Record<string, unknown> = {};
    if (productInfo) {
      data = {
        // for test cases the id can be empty
        transaction_id: paymentInfo?.contractId,
        currency: productInfo.price.currency,
        value: productInfo.price.period.periodPrice / 100,
        coupon: `VARIANT_${blueprintVariant}`,
        items: [
          {
            item_id: productInfo.blueprintId,
            item_category: productInfo.blueprintGroupId,
            item_name: productInfo.bpGroupName,
            price: productInfo.price.period.periodPrice / 100,
            coupon: `VARIANT_${blueprintVariant}`,
            quantity: 1,
          },
        ],
      };
    }
    this.trackEvent('purchase', data, callback);
  }

  public trackAddToCartEvent(callback?: () => void): void {
    const blueprintVariant = Session.instance.get(
      SessionInfo.purchase_blueprintVariant,
    );
    const productInfo = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    let data: Record<string, unknown> = {};
    if (productInfo) {
      data = {
        currency: productInfo.price.currency,
        value: productInfo.price.period.periodPrice / 100,
        items: [
          {
            item_id: productInfo.blueprintId,
            item_category: productInfo.blueprintGroupId,
            item_name: productInfo.bpGroupName,
            price: productInfo.price.period.periodPrice / 100,
            coupon: `VARIANT_${blueprintVariant}`,
            quantity: 1,
          },
        ],
      };
    }
    this.trackEvent('add_to_cart', data, callback);
  }

  public trackBeginCheckoutEvent(callback?: () => void): void {
    const blueprintVariant = Session.instance.get(
      SessionInfo.purchase_blueprintVariant,
    );
    const productInfo = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    let data: Record<string, unknown> = {};
    if (productInfo) {
      data = {
        currency: productInfo.price.currency,
        value: productInfo.price.period.periodPrice / 100,
        coupon: `VARIANT_${blueprintVariant}`,
        items: [
          {
            item_id: productInfo.blueprintId,
            item_category: productInfo.blueprintGroupId,
            item_name: productInfo.bpGroupName,
            price: productInfo.price.period.periodPrice / 100,
            coupon: `VARIANT_${blueprintVariant}`,
            quantity: 1,
          },
        ],
      };
    }
    this.trackEvent('begin_checkout', data, callback);
  }
}
